/**
 * Exo queue ranking + eligibility gates (FE-EXO-2, FE-EXO-5, FE-EXO-7).
 */

import type { ControlBridgeQueueItem } from '../deriveViewModel';
import { isP0, priorityRank } from '../queueHelpers';

export interface ExoTransitionFlags {
  /** Model conditions met AND resource available (FE-EXO-5). */
  eligible: boolean;
  /** Resource availability final gate. */
  resourceAvailable: boolean;
  /** Full Bridge Session required → route to Chat (FE-EXO-7). */
  requiresBridgeSession: boolean;
}

function truthyFlag(value: unknown, defaultValue: boolean): boolean {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true' || v === '1' || v === 'yes') return true;
    if (v === 'false' || v === '0' || v === 'no') return false;
  }
  return defaultValue;
}

export function deriveExoTransitionFlags(item: ControlBridgeQueueItem): ExoTransitionFlags {
  const resourceAvailable = truthyFlag(
    (item as ControlBridgeQueueItem & { resourceAvailable?: boolean }).resourceAvailable,
    true
  );
  const requiresBridgeSession = truthyFlag(
    (item as ControlBridgeQueueItem & { requiresBridgeSession?: boolean }).requiresBridgeSession,
    false
  );
  const gateMet = item.gateMet;
  const conditionsMet = gateMet === false ? false : true;
  const eligible = conditionsMet && resourceAvailable && !requiresBridgeSession;
  return { eligible, resourceAvailable, requiresBridgeSession };
}

/**
 * Rank order (FE-EXO-2):
 * 1. P0 issues
 * 2. time-sensitive (statusDate / endDate soon — approximated by recommended + statusDate)
 * 3. waypoint sequenceOrder
 * 4. dependency order (fewer unmet deps first)
 * 5. residual priority rank
 */
export function compareExoQueueItems(a: ControlBridgeQueueItem, b: ControlBridgeQueueItem): number {
  const aIsIssue = Boolean(a.issueType || a.issueSummary);
  const bIsIssue = Boolean(b.issueType || b.issueSummary);
  const aP0 = isP0(a.priority);
  const bP0 = isP0(b.priority);

  if (aIsIssue && aP0 && !(bIsIssue && bP0)) return -1;
  if (bIsIssue && bP0 && !(aIsIssue && aP0)) return 1;

  const aTime = timeSensitiveScore(a);
  const bTime = timeSensitiveScore(b);
  if (aTime !== bTime) return aTime - bTime;

  const aSeq = a.sequenceOrder ?? Number.POSITIVE_INFINITY;
  const bSeq = b.sequenceOrder ?? Number.POSITIVE_INFINITY;
  if (aSeq !== bSeq) return aSeq - bSeq;

  const aDeps = (a.dependsOnRefs ?? []).length;
  const bDeps = (b.dependsOnRefs ?? []).length;
  if (aDeps !== bDeps) return aDeps - bDeps;

  const pr = priorityRank(a.priority) - priorityRank(b.priority);
  if (pr !== 0) return pr;

  if (a.recommended && !b.recommended) return -1;
  if (b.recommended && !a.recommended) return 1;

  return (a.title || '').localeCompare(b.title || '');
}

function timeSensitiveScore(item: ControlBridgeQueueItem): number {
  // Lower = more urgent. Prefer ISO endDate / statusDate when present.
  const end = item.endDate?.trim();
  const status = item.statusDate?.trim();
  const iso = end || status;
  if (iso && /^\d{4}-\d{2}-\d{2}/.test(iso)) {
    const t = Date.parse(iso.slice(0, 10));
    if (Number.isFinite(t)) return t;
  }
  if (item.recommended) return Number.MAX_SAFE_INTEGER - 2;
  return Number.MAX_SAFE_INTEGER;
}

/** Rank and filter to Exo-actionable items (eligible + not bridge-session). */
export function rankExoQueue(items: ControlBridgeQueueItem[]): ControlBridgeQueueItem[] {
  return [...items]
    .filter((item) => deriveExoTransitionFlags(item).eligible)
    .sort(compareExoQueueItems);
}

/** Items that must divert to Chat (bridge session) rather than Exo Complete. */
export function exoBridgeSessionDiversions(items: ControlBridgeQueueItem[]): ControlBridgeQueueItem[] {
  return items.filter((item) => deriveExoTransitionFlags(item).requiresBridgeSession);
}
